import { ChangeEvent, useCallback, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { object, string } from 'yup';
import { useTranslation } from 'react-i18next';

import { Feedback, Flex, FormField, TextInput } from '@percent/lemonade';
import { Card } from '@percent/hosted-validation-form/ui';
import { useValidationStepData } from '../../validationStepContext/useValidationStepData/useValidationStepData';
import { getStepAfterEmail } from '../stepFlow';
import { emailRegex } from '@percent/utility';
import { ClientResponse } from '@percent/hosted-validation-form/api/types/types';
import { AdvancedEmailValidationResponse } from './Email.types';
import {
  useDebouncedEffect,
  useQuery,
  useProgress,
  usePartnerConfig,
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS,
} from '@percent/hosted-validation-form/hooks';

type FormFieldValues = {
  email: string;
  website?: string;
  withoutWebsite?: boolean;
  differentEmail?: string;
};

const DEBOUNCE_TIME = 1500;

export const Email = () => {
  const { handleNext, handleBack } = useValidationStepData();
  const { t } = useTranslation();
  const advancedEmailValidation = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.HVF_ADVANCED_EMAIL_VALIDATION);
  const strictAdvancedEmailValidation = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.HVF_STRICT_ADVANCED_EMAIL_VALIDATION,
  );

  const {
    getValues,
    formState: { errors, dirtyFields },
    setValue,
    setError,
    clearErrors,
  } = useFormContext<FormFieldValues>();
  const { partnerConfig } = usePartnerConfig();
  const { suggestOrgEmail, suggestOrgEmailTip } = partnerConfig?.configuration ?? {};
  const { progress } = useProgress();

  const [isEmailValid, setIsEmailValid] = useState<boolean | null>(null);

  const email = useWatch({ name: 'email' });

  const {
    data: advancedEmailValidationData,
    isError: advancedEmailValidationError,
    isFetching: isLoadingAdvancedEmailValidation,
    refetch: refetchAdvancedEmailValidation,
  } = useQuery<
    Record<string, unknown>,
    ClientResponse<AdvancedEmailValidationResponse>,
    unknown,
    ClientResponse<AdvancedEmailValidationResponse>
  >(
    ['advancedEmailValidation', email],
    (args) => ({
      endpoint: 'v1/advanced-email-validation/results',
      args: {
        email,
        ...(args ?? {}),
      },
    }),
    {
      enabled: false,
      retry: false,
      onSuccess: (data) => {
        if (strictAdvancedEmailValidation && !data?.data?.result?.isValid) {
          setError('email', {
            type: 'custom',
            message: t('hostedValidation.validation.strictAdvancedEmailValidation'),
          });
        } else {
          setIsEmailValid(data?.data?.result?.isValid);
        }
      },
    },
  );

  const handleAdvancedEmailValidation = useCallback(() => {
    if (!errors.email && !!email && isEmailValid === null && advancedEmailValidation) {
      refetchAdvancedEmailValidation();
    }
  }, [errors.email, email, isEmailValid, refetchAdvancedEmailValidation, advancedEmailValidation]);

  useDebouncedEffect(
    () => {
      handleAdvancedEmailValidation();
    },
    [email],
    DEBOUNCE_TIME,
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleAdvancedEmailValidation();
    }
  };

  const handleNavigateNext = () => {
    const email = getValues('email');
    setValue('email', email);

    handleNext(getStepAfterEmail(partnerConfig));
  };

  const reValidateEmail = (emailValue = getValues('email')) => {
    setIsEmailValid(null);
    clearErrors('email');

    if (emailValue) {
      if (emailValue.length >= 255) {
        setError(
          'email',
          {
            type: 'max',
            message: t('hostedValidation.validation.maxLength', {
              fieldName: t('hostedValidation.label.email'),
              maxCharacterLimit: 255,
              interpolation: { escapeValue: false },
            }),
          },
          { shouldFocus: true },
        );
      } else {
        const emailSchema = object().shape({
          email: string().matches(emailRegex, t('hostedValidation.validation.emailValid')),
        });

        emailSchema
          .isValid({
            email: emailValue,
          })
          .then((valid) => {
            if (!valid) {
              setError(
                'email',
                { type: 'custom', message: t('hostedValidation.validation.emailValid') },
                { shouldFocus: true },
              );
            }
          });
      }
    } else {
      setError(
        'email',
        {
          type: 'required',
          message: t('hostedValidation.validation.required', {
            fieldName: t('hostedValidation.label.email'),
            interpolation: { escapeValue: false },
          }),
        },
        { shouldFocus: true },
      );
    }
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    reValidateEmail(e.target.value);

    setValue('email', e.target.value, {
      shouldValidate: false,
      shouldDirty: true,
    });
  };

  return (
    <Card
      onBack={handleBack}
      title={t('hostedValidation.title.verifyEmail')}
      description={suggestOrgEmail ? t('hostedValidation.title.enterOrgEmail') : t('hostedValidation.title.enterEmail')}
      progress={progress}
      nextButtonDisabled={
        !!errors?.email?.message ||
        !dirtyFields?.email ||
        (advancedEmailValidation ? !advancedEmailValidationData && !advancedEmailValidationError : false)
      }
      nextButtonLabel={t('hostedValidation.button.next')}
      isLoading={isLoadingAdvancedEmailValidation}
      onNext={handleNavigateNext}
    >
      <Flex direction="column" gap={16} w="100%">
        <FormField
          label={t('hostedValidation.label.email')}
          status={errors.email?.message ? 'danger' : 'default'}
          necessity="required"
          statusMessage={errors.email?.message}
          description={suggestOrgEmail ? suggestOrgEmailTip : undefined}
        >
          <>
            <TextInput
              name="email"
              value={email}
              onChange={handleEmailChange}
              onBlur={handleAdvancedEmailValidation}
              onKeyDown={handleKeyDown}
              placeholder={t('hostedValidation.placeholder.email')}
            />
            {isEmailValid === false && (
              <Feedback variant="informative">{t('hostedValidation.validation.advancedEmailValidation')}</Feedback>
            )}
          </>
        </FormField>
      </Flex>
    </Card>
  );
};
